import type {
  CustomInfoType,
  CreateCustomInfoTypeRequest,
  UpdateCustomInfoTypeRequest,
  CustomInfoTypeDetectionType,
  CustomInfoTypeLikelihood,
} from "$lib/models/base";
import { makeApiRequest } from "$lib/utils/api";

/**
 * Convert API response (snake_case) to frontend interface (camelCase)
 */
function mapApiResponseToCustomInfoType(apiResponse: Record<string, unknown>): CustomInfoType {
  return {
    id: apiResponse.id as string,
    name: apiResponse.name as string,
    displayName: (apiResponse.display_name || apiResponse.displayName) as string,
    description: (apiResponse.description as string | null) || null,
    detectionType: (apiResponse.detection_type || apiResponse.detectionType) as CustomInfoTypeDetectionType,
    regexPattern: (apiResponse.regex_pattern || apiResponse.regexPattern) as string | null,
    dictionaryWords: (apiResponse.dictionary_words || apiResponse.dictionaryWords) as string[] | null,
    likelihood: (apiResponse.likelihood as CustomInfoTypeLikelihood) || "LIKELY",
    enabled: apiResponse.enabled as boolean,
    created: (apiResponse.created as string | null) || null,
    modified: (apiResponse.modified as string | null) || null,
  };
}

/**
 * List all custom infoTypes for an organization
 */
export async function listCustomInfoTypes(orgId: string): Promise<CustomInfoType[]> {
  const response = await makeApiRequest(`/orgs/${orgId}/custom-info-types/`, {}, "GET");
  if (!response || !Array.isArray(response)) {
    return [];
  }
  return response.map(mapApiResponseToCustomInfoType);
}

/**
 * Get a single custom infoType by ID
 */
export async function getCustomInfoType(
  orgId: string,
  infoTypeId: string
): Promise<CustomInfoType> {
  const response = await makeApiRequest(
    `/orgs/${orgId}/custom-info-types/${infoTypeId}`,
    {},
    "GET"
  );
  return mapApiResponseToCustomInfoType(response);
}

/**
 * Create a new custom infoType
 */
export async function createCustomInfoType(
  orgId: string,
  data: CreateCustomInfoTypeRequest
): Promise<CustomInfoType> {
  // Convert camelCase to snake_case for the API
  const apiData = {
    name: data.name,
    display_name: data.displayName,
    description: data.description,
    detection_type: data.detectionType,
    regex_pattern: data.regexPattern,
    dictionary_words: data.dictionaryWords,
    likelihood: data.likelihood || "LIKELY",
    enabled: data.enabled !== undefined ? data.enabled : true,
  };

  const response = await makeApiRequest(
    `/orgs/${orgId}/custom-info-types/`,
    apiData,
    "POST"
  );
  return mapApiResponseToCustomInfoType(response);
}

/**
 * Update an existing custom infoType
 */
export async function updateCustomInfoType(
  orgId: string,
  infoTypeId: string,
  data: UpdateCustomInfoTypeRequest
): Promise<CustomInfoType> {
  // Convert camelCase to snake_case for the API, only include defined fields
  const apiData: Record<string, unknown> = {};
  if (data.displayName !== undefined) apiData.display_name = data.displayName;
  if (data.description !== undefined) apiData.description = data.description;
  if (data.regexPattern !== undefined) apiData.regex_pattern = data.regexPattern;
  if (data.dictionaryWords !== undefined) apiData.dictionary_words = data.dictionaryWords;
  if (data.likelihood !== undefined) apiData.likelihood = data.likelihood;
  if (data.enabled !== undefined) apiData.enabled = data.enabled;

  const response = await makeApiRequest(
    `/orgs/${orgId}/custom-info-types/${infoTypeId}`,
    apiData,
    "PUT"
  );
  return mapApiResponseToCustomInfoType(response);
}

/**
 * Delete a custom infoType
 */
export async function deleteCustomInfoType(
  orgId: string,
  infoTypeId: string
): Promise<void> {
  await makeApiRequest(`/orgs/${orgId}/custom-info-types/${infoTypeId}`, {}, "DELETE");
}

/**
 * Toggle enabled status of a custom infoType
 */
export async function toggleCustomInfoType(
  orgId: string,
  infoTypeId: string,
  enabled: boolean
): Promise<CustomInfoType> {
  return updateCustomInfoType(orgId, infoTypeId, { enabled });
}
